import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Icon } from '../components/ui';
import BadgeMedal from '../components/BadgeMedal';
import { BADGE_CATALOG, checkAndAwardBadges, metricsFrom } from '../lib/badges';
import { formatDate } from '../lib/dates';
import { useBodyDays, useEarnedBadges, useNutritionDays, useProgram, useProgressDays, useWaterDays } from '../lib/useUserData';

// Mirrors the app's BadgesPage card: earned badges in full color with the date
// earned, locked ones dimmed with progress toward the threshold.
function BadgeCard({ badge, earned, earnedAt, count, value }) {
  const t = useT();
  const progress = Math.min(value || 0, badge.threshold);
  return (
    <div className={`gm-badge${earned ? ' earned' : ' locked'}`} style={{ '--badge-color': badge.color }}>
      <BadgeMedal badge={badge} earned={earned} size={68} count={count} />
      <div className="gm-badge__title">{t.badgeTitle(badge.id)}</div>
      <div className="gm-badge__desc">{t.badgeDescription(badge.id)}</div>
      <div className="gm-badge__foot">
        {earned ? (
          <>
            <span className="gm-badge__earned">
              <Icon name="check_circle" size={14} />
              {count > 1 ? t('badgeEarnedTimesLabel', count) : t('badgeEarnedLabel')}
            </span>
            {earnedAt && <span className="gm-badge__date">{formatDate(earnedAt, t.locale)}</span>}
          </>
        ) : (
          <>
            <div className="gm-progress gm-progress--badge"><span style={{ width: `${(progress / badge.threshold) * 100}%` }} /></div>
            <span className="gm-badge__progress">{t('badgeProgressLabel', progress, badge.threshold)}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function Badges() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const { program } = useProgram(user.exerciseProgramID);
  const { progressDays } = useProgressDays(user.id);
  const { nutritionDays } = useNutritionDays(user.id);
  const { bodyDays } = useBodyDays(user.id);
  const { waterByDate } = useWaterDays(user.id);
  const { earned, counts } = useEarnedBadges(user.id);
  // Awards normally happen on a write, so a badge whose condition history already
  // meets (e.g. one added in an update) would sit locked behind a full bar until then.
  useEffect(() => {
    checkAndAwardBadges(user.id).catch(() => {});
  }, [user.id]);
  const metrics = useMemo(() => {
    const waterDays = Object.entries(waterByDate).map(([id, ml]) => ({ id, ml }));
    return metricsFrom({ progressDays, nutritionDays, bodyDays, waterDays, program, userData: user });
  }, [progressDays, nutritionDays, bodyDays, waterByDate, program, user]);
  const earnedCount = BADGE_CATALOG.filter((b) => b.id in earned).length;
  return (
    <div className="gm-content gm-enter">
      <div className="gm-page-head">
        <button type="button" className="gm-icon-btn" onClick={() => navigate('/home')} title={t('back')}><Icon name="arrow_back" /></button>
        <h1 className="gm-page-title">{t.upper(t('badgesPageTitle'))}</h1>
        <span className="gm-pill">{earnedCount} / {BADGE_CATALOG.length}</span>
      </div>
      <div className="gm-badge-grid">
        {BADGE_CATALOG.map((b) => (
          <BadgeCard key={b.id} badge={b} earned={b.id in earned} earnedAt={earned[b.id]} count={counts[b.id] || 0} value={metrics[b.metric]} />
        ))}
      </div>
    </div>
  );
}
